import './SensorsExplorerPage.css';

import React, { useState, useEffect, createContext, useContext } from 'react';
import Banner from '../../b2b-common/Banner';
import { useLoginContext } from '../../../App';
import { useContentContext } from '../../layout/Layout';
import { showNotification } from '../../b2b-common/Notification';
import { fetchOrganizationSensors, fetchSamplingLevelN_FwConfigurations_byFwId } from '../../../services/b2b-iotsampling-apiService';
import { fetchAPIDataWhileSettingState, fullImagePath, shouldRenderComponent } from '../../b2b-common/helpers';
import { fetchFrameworksData } from '../../../services/b2b-frameworks-apiService';
import WaitingSpinner from '../../b2b-common/WaitingSpinner';
import GenericTabs from '../../b2b-common/GenericTabs';
import B2BElementsExplorerInMemory from '../../b2b-common/B2BElementsExplorerInMemory';
import SensorCard from '../components/SensorCard';
import ProtectedElement from '../../b2b-common/ProtectedElement';
import CustomButton from '../../b2b-common/CustomButton';
import { AddCircleOutline } from '@mui/icons-material';
import EditSensorModal from '../forms/EditSensorModal';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';

const IotFrameworksContext = createContext();

const SensorsExplorerPage = () => {

    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();
    const { t } = useB2BTranslation('b2b-sampling');

    const [iotFrameworks, setIotFrameworks] = useState(null);
    const [loadingIotFrameworks, setLoadingIotFrameworks] = useState(false);
    const [currentFramework, setCurrentFramework] = useState(null);
    const [currentFwMasterConfigs, setCurrentFwMasterConfigs] = useState(null);
    const [sensorConfigs, setSensorConfigs] = useState(null);
    const [loadingSensorConfigs, setLoadingSensorConfigs] = useState(false);
    const [page, setPage] = useState(1);
    const [showSensorModal, setShowSensorModal] = useState(false);

    const fetchIotFrameworks = async () => {
        const paramsArray = [
            'iot', loginData.accessToken,
            true, // fetch images
            true, // include lelvel1s
        ];
        try {
            const iotFws = await fetchAPIDataWhileSettingState(fetchFrameworksData, paramsArray, setLoadingIotFrameworks);
            setIotFrameworks(iotFws);
            if (iotFws && iotFws.length > 0) {
                // console.log('currentFramework:', iotFws[0]);
                setCurrentFramework(iotFws[0]);
            }
            const fwId = iotFws && iotFws.length > 0 ? iotFws[0].frameworkId : null;
            const masterConfigs = await fetchSamplingLevelN_FwConfigurations_byFwId(loginData.accessToken, fwId);
            setCurrentFwMasterConfigs(masterConfigs);
        } catch (error) {
            showNotification(setNotification, t('Error fetching IoT Frameworks: ') + error.message, 'error', 5000);
        } finally {
            setLoadingIotFrameworks(false);
        }
    }

    const getOrgSensorData = async () => {
        try {
            const orgSensors = await fetchAPIDataWhileSettingState(fetchOrganizationSensors, [loginData.accessToken, loginData.loggedOrganizationUuid], setLoadingSensorConfigs);
            setSensorConfigs(orgSensors);
        } catch (error) {
            showNotification(setNotification, t('Error fetching Organization Sensors: ') + error.message, 'error', 5000);
        } finally {
            setLoadingSensorConfigs(false);
        }
    }

    useEffect(() => {
        if (loginData) {
            fetchIotFrameworks();
            getOrgSensorData();
        }
    }, [loginData, setNotification]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    }

    const handleSavedSensor = (newSensor) => {
        getOrgSensorData();
        setShowSensorModal(false);
    }

    const handleDeletedSensor = (sensorId) => {
        getOrgSensorData();
    }

    const tabsConfig = iotFrameworks && iotFrameworks.map(fw => {
        return {
            key: fw.frameworkId,
            label: fw.frameworkName,
            content: (
                <IotFrameworksContext.Provider value={{ currentFramework, currentFwMasterConfigs }}>
                    <div className="framework-related-sensors-header">
                        <div className="framework-image">
                            <img src={fullImagePath(fw.imagePath)} alt={fw.frameworkName} />
                        </div>
                        <div className="framework-related-sensors-description">
                            {fw.frameworkDescription}
                        </div>
                    </div>
                    <div className="framework-related-sensors-main-content">
                        <Banner message={t('Current sensors from this brand')} headingLevel='h3' underline={false} />
                        <CustomButton icon={<AddCircleOutline />} label={t('Configure new sensor')}
                            onClick={() => setShowSensorModal(true)}
                            rolesAllowed={['ADMIN']}
                            className={'configure-new-sensor-button white white_02'}
                        />
                        <B2BElementsExplorerInMemory
                            numRows={6}
                            elementsData={{ content: sensorConfigs }}
                            className='framework-related-sensors'
                            ElementComponent={SensorCard}
                            elementComponentPropName={'sensor'}
                            elementComponentHandlers={{
                                onDelete: handleDeletedSensor,
                                onSave: handleSavedSensor,
                            }}
                            currentPage={page}
                            handlePageChange={handlePageChange}
                        />
                    </div>
                    <EditSensorModal open={showSensorModal}
                        onCancel={() => setShowSensorModal(false)}
                        onSave={handleSavedSensor}
                    />
                </IotFrameworksContext.Provider>
            )
        }
    });

    return (
        <ProtectedElement rolesAllowed={['ADMIN']} b2bProduct={['B2B_IOT_SAMPLING', 'B2B_IOT_FW']}>
            <div className="sensors-explorer-page">
                <Banner message={t('Organization Sensors')} headingLevel='h1' underline={false} />
                {(loadingIotFrameworks || loadingSensorConfigs) && (
                    <WaitingSpinner text={t('Loading Sensors...')} />
                )}
                {!loadingIotFrameworks && !loadingSensorConfigs && currentFramework && (
                    <GenericTabs tabsConfig={tabsConfig}
                        className="sensors-explorer-frameworks"
                    />
                )}
            </div>
        </ProtectedElement>
    );
};

export default SensorsExplorerPage;
export { IotFrameworksContext };
export const useIotFreworksContext = () => useContext(IotFrameworksContext);